import { Container as DashContainer } from "@components/buttons/common/AnimatedDash"
import LinkButton from "@components/buttons/LinkButton"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14Styles,
  body16MainStyles,
  body18Styles,
  body28Styles,
  h2Styles,
  h3Styles,
  h4Styles,
  paragraph18Styles,
} from "@constants/typography"
import { getIn } from "@utils/index"
import { MobileOnly, TabletAndDesktopOnly } from "@utils/MediaQuery"
import React from "react"
import styled from "styled-components"

type DescriptionAlignments = "bottom" | "center" | "top"

const descriptionAlignmentPropMap = {
  bottom: "flex-end",
  center: "center",
  top: "flex-start",
}

const Container = styled.div`
  padding: 44px 0;

  @media ${breakpoints.tablet} {
    padding: 56px calc(1 / 12 * 100vw) 78px;
  }

  @media ${breakpoints.laptop} {
    padding: 104px calc(1 / 12 * 100vw) 94px;
  }
`

const Content = styled.div`
  @media ${breakpoints.tablet} {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  @media ${breakpoints.laptop} {
    display: flex;
  }
`

const FirstColumn = styled.div`
  @media ${breakpoints.tablet} {
    width: calc(4.5 / 12 * 100vw);
    flex-grow: 0;
    flex-shrink: 0;
  }

  @media ${breakpoints.laptop} {
    width: calc(2.5 / 12 * 100vw);
    margin-right: calc(0.5 / 12 * 100vw);
  }
`

const SecondColumn = styled.div`
  @media ${breakpoints.tablet} {
    width: calc(4.5 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    width: calc(2.5 / 12 * 100vw);
    margin-right: calc(0.5 / 12 * 100vw);
    flex-grow: 0;
    flex-shrink: 0;
  }
`

const ThirdColumn = styled.div<{
  descriptionAlignment?: DescriptionAlignments
  noFeatures: boolean
}>`
  @media ${breakpoints.tablet} {
    width: calc(10 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    display: flex;
    width: calc(4 / 12 * 100vw);
    flex-direction: column;
    ${({ descriptionAlignment, noFeatures }) =>
      noFeatures &&
      descriptionAlignment &&
      `
      justify-content: ${descriptionAlignmentPropMap[descriptionAlignment]};
    `}
  }
`

const Caption = styled.div`
  ${body14Styles}
  display: flex;
  margin-top: 20px;
  padding: 0 20px;
  align-items: center;
  color: ${colors.mediumGray};

  @media ${breakpoints.tablet} {
    margin-top: 18px;
    padding: 0;
  }

  @media ${breakpoints.laptop} {
    margin-top: 32px;
    padding: 0;
  }

  @media ${breakpoints.desktop} {
    ${body16MainStyles}
    margin-top: 32px;
    color: ${colors.mediumGray};
  }
`

const CaptionDash = styled.div`
  width: 23px;
  height: 1px;
  background-color: ${colors.mediumGray};
  margin-right: 12px;
`

const PrimaryImage = styled.div`
  ${ImageHelperContainer} {
    height: calc(100vw * 1.34);

    @media ${breakpoints.tablet} {
      height: auto;
      min-height: calc(4.5 / 12 * 100vw * 2);
    }

    @media ${breakpoints.laptop} {
      height: auto;
      min-height: calc(3 / 12 * 100vw * 2);
    }
  }
`

const SecondaryImage = styled.div`
  ${ImageHelperContainer} {
    height: calc((100vw - 40px) * 1.14);
    margin: 0 20px;

    @media ${breakpoints.tablet} {
      height: auto;
      min-height: calc(3 / 12 * 100vw * 1.56);
      margin: 0;
    }

    @media ${breakpoints.laptop} {
      height: auto;
      min-height: calc(3 / 12 * 100vw * 1.56);
      margin: 0;
    }
  }
`

const Header = styled.h2`
  ${h4Styles}
  margin-bottom: 30px;
  padding: 0 20px;

  @media ${breakpoints.tablet} {
    ${h3Styles}
    margin-bottom: 52px;
    padding: 0;
  }

  @media ${breakpoints.laptop} {
    ${h3Styles}
    margin-bottom: 79px;
    padding: 0;
  }

  @media ${breakpoints.desktop} {
    ${h2Styles}
    margin-bottom: 79px;
  }
`

const Description = styled.div`
  ${body18Styles}
  margin: 63px 0 48px;
  padding: 0 20px;

  @media ${breakpoints.tablet} {
    ${body28Styles}
    width: calc(8 / 12 * 100vw);
    margin: 80px 0 72px;
    padding: 0;
  }

  @media ${breakpoints.laptop} {
    ${paragraph18Styles}
    margin: 32px 0 48px;
    padding: 0;
  }

  @media ${breakpoints.desktop} {
    ${body28Styles}
    margin: 32px 0 48px;
  }
`

const Features = styled.div`
  display: flex;
  margin: 49px 0 26px;
  padding: 0 20px;
  flex-wrap: wrap;

  @media ${breakpoints.tablet} {
    margin: 0 0 38px;
    padding: 0;
  }

  @media ${breakpoints.laptop} {
    margin: 0 0 38px;
    padding: 0;
  }
`

const Feature = styled.div`
  width: calc(1 / 2 * (100vw - 64px));
  margin-bottom: 40px;
  padding: 0 32px 0 16px;

  @media ${breakpoints.tablet} {
    width: calc(2 / 12 * 100vw);
    margin-right: calc(1 / 12 * 100vw);
    padding: 0; // padding-left 4px on text
  }

  @media ${breakpoints.laptop} {
    width: calc(1 / 12 * 100vw);
    margin-right: 12px;
    padding: 0 16px 0 0;
  }
`

const FeatureIcon = styled.div`
  ${ImageHelperContainer} {
    width: 48px;
    height: 48px;
    margin-bottom: 20px;

    @media ${breakpoints.tablet} {
      width: 40px;
      height: 40px;
      margin-bottom: 16px;
    }

    @media ${breakpoints.laptop} {
      margin-bottom: 16px;
    }
  }
`

const FeatureText = styled.div`
  ${body14Styles}
  color: ${colors.mediumDarkGray};
  line-height: 20px;

  @media ${breakpoints.laptop} {
    padding-right: 12px;
  }
`

const LinkWrapper = styled.div`
  width: 100%;
  padding: 0 20px;

  @media ${breakpoints.tablet} {
    display: flex;
    padding: 0;
    flex-wrap: wrap;
  }

  @media ${breakpoints.laptop} {
    display: flex;
    padding: 0;
    flex-wrap: wrap;
  }

  ${DashContainer} {
    margin-left: 24px;
  }
`

const Spacer = styled.div`
  width: 100%;
  height: 48px;
`

export interface FeatureProps {
  description: {
    description: string
  }
  icon: string
}

interface TwoImageFeaturesCtaProps {
  blockId?: string
  ctaLink: string
  ctaText: string
  ctaTextMobile: string
  description: string
  descriptionAlignment?: DescriptionAlignments
  featuresList: Array<FeatureProps>
  header: string
  primaryImage: string
  primaryImageCaption: string
  secondaryImage: string
  secondaryImageCaption: string
}

const TwoImageFeaturesCta: React.FC<TwoImageFeaturesCtaProps> = ({
  blockId,
  ctaLink,
  ctaText,
  ctaTextMobile,
  description,
  descriptionAlignment,
  featuresList,
  header,
  primaryImage,
  primaryImageCaption,
  secondaryImage,
  secondaryImageCaption,
}) =>
  primaryImage && secondaryImage ? (
    <Container id={blockId || undefined}>
      <Header>{header}</Header>
      <Content>
        <FirstColumn>
          <PrimaryImage>
            <ImageHelper src={primaryImage} />
          </PrimaryImage>
          <Caption>
            <CaptionDash />
            {primaryImageCaption}
          </Caption>
        </FirstColumn>
        <MobileOnly>
          <Description>{description}</Description>
        </MobileOnly>
        <SecondColumn>
          <SecondaryImage>
            <ImageHelper src={secondaryImage} />
          </SecondaryImage>
          <Caption>
            <CaptionDash />
            {secondaryImageCaption}
          </Caption>
        </SecondColumn>
        <ThirdColumn
          descriptionAlignment={descriptionAlignment}
          noFeatures={featuresList.length === 0}
        >
          <TabletAndDesktopOnly>
            <Description>{description}</Description>
          </TabletAndDesktopOnly>
          {featuresList.length > 0 && (
            <Features>
              {featuresList.map((feature: FeatureProps, idx: number) => (
                <Feature key={idx}>
                  <FeatureIcon>
                    <ImageHelper src={getIn(feature, "icon.file.url")} />
                  </FeatureIcon>
                  <FeatureText>
                    {getIn(feature, "description.description")}
                  </FeatureText>
                </Feature>
              ))}
            </Features>
          )}
          <MobileOnly>
            {featuresList.length === 0 && <Spacer />}
            <LinkWrapper>
              <LinkButton text={ctaTextMobile} to={ctaLink} />
            </LinkWrapper>
          </MobileOnly>
          <TabletAndDesktopOnly>
            <LinkWrapper>
              <LinkButton text={ctaText} to={ctaLink} />
            </LinkWrapper>
          </TabletAndDesktopOnly>
        </ThirdColumn>
      </Content>
    </Container>
  ) : null

export default TwoImageFeaturesCta
